import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Eye, Info, ListChecks, Maximize2, Star, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../../common/httpclient';

// Detalhe do template de checklist — dialog central somente-leitura,
// DESIGN-SYSTEM.md §5.6 "Center modal", com o `detail-grid` copiado 1:1 do
// mesmo padrão já usado em `pages/services/View.js`
// (`prototipo-lista-servicos.html`), conforme o próprio comentário daquele
// arquivo antecipa para esta tela (FRONTEND-TASKS.md "Templates de checklist
// — lista"). Mostra só um resumo (contagem de grupos/itens); continua sendo
// apenas um preview rápido — a estrutura completa (todos os grupos + todos
// os itens + chips de resposta OK/NOK/Manutenção) agora vive na página
// dedicada "Visualização de template" (item 132, `View.js`, rota
// `/checklist/:id/view`), para onde o botão "Ver detalhes completos" abaixo
// navega. O editor de grupos arrastar-e-soltar (`GroupForm.js`, item 130) é
// apenas referenciado aqui via aviso + botão stub.
function DetailItem({ label, value, muted = false, full = false }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto' }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '4px' }}>
                {label}
            </Typography>
            {typeof value === 'string' ? (
                <Typography sx={{ fontSize: '13.5px', fontWeight: muted ? 500 : 600, color: muted ? 'text.disabled' : 'text.primary', lineHeight: 1.4 }}>
                    {value}
                </Typography>
            ) : (
                <Box sx={{ mt: '2px' }}>{value}</Box>
            )}
        </Box>
    );
}

function DefaultPill() {
    return (
        <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '11px',
            fontWeight: 700,
            lineHeight: 1,
            padding: '4px 10px',
            borderRadius: '20px',
            bgcolor: 'success.bg',
            color: 'success.main',
        }}>
            <Star size={11} strokeWidth={0} fill="currentColor" />
            Sim, é o template padrão
        </Box>
    );
}

export default function ChecklistViewDialog({ open, onClose, checklistId, canEdit = true, onRequestEdit, onStubGroupsEditor }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [checklist, setChecklist] = useState({});
    const [groupsSummary, setGroupsSummary] = useState({ groupsCount: 0, itemsCount: 0 });

    const fetchChecklist = async (id) => {
        try {
            setLoading(true);
            const [checklistResponse, viewResponse] = await Promise.all([
                httpClient.get(`/checklist/${id}`),
                httpClient.get(`/checklist/${id}/view`).catch(() => null),
            ]);
            setChecklist(checklistResponse.data || {});

            const groups = Array.isArray(viewResponse?.data) ? viewResponse.data : [];
            const groupsCount = groups.length;
            const itemsCount = groups.reduce((sum, group) => {
                const items = Array.isArray(group.group_inspection_items)
                    ? group.group_inspection_items.filter((i) => i.active !== false)
                    : [];
                return sum + items.length;
            }, 0);
            setGroupsSummary({ groupsCount, itemsCount });
        } catch (error) {
            console.error('Erro ao carregar dados do template de checklist:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (checklistId && open) {
            fetchChecklist(checklistId);
        }
    }, [checklistId, open]);

    const handleClose = () => {
        setChecklist({});
        setGroupsSummary({ groupsCount: 0, itemsCount: 0 });
        setLoading(true);
        onClose?.();
    };

    const groupsLabel = groupsSummary.groupsCount > 0
        ? `${groupsSummary.groupsCount} grupo${groupsSummary.groupsCount === 1 ? '' : 's'} · ${groupsSummary.itemsCount} ite${groupsSummary.itemsCount === 1 ? 'm' : 'ns'} no total`
        : 'Nenhum grupo de inspeção adicionado ainda';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
        >
            <Box sx={{
                px: '22px',
                pt: '20px',
                pb: '14px',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.25,
            }}>
                <Box>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'info.bg',
                        color: 'primary.main',
                        mb: '10px',
                    }}>
                        <Eye size={18} strokeWidth={2} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                        {loading ? 'Carregando…' : (checklist.name || '—')}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        Detalhes do template de checklist.
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <X size={15} strokeWidth={2} />
                </IconButton>
            </Box>

            <Box sx={{ p: '18px 22px', maxHeight: '60vh', overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' }}>
                            <DetailItem full label="Descrição" value={checklist.description || 'Nenhuma descrição fornecida'} muted={!checklist.description} />
                            <DetailItem label="Template padrão" value={checklist.is_default ? <DefaultPill /> : 'Não'} muted={!checklist.is_default} />
                            <DetailItem label="Grupos de inspeção" value={groupsLabel} muted={groupsSummary.groupsCount === 0} />
                        </Box>

                        {/* groups-note — deixa explícito que a montagem/reordenação
                            de grupos e itens é uma tela separada (item "Templates
                            de checklist — editor", `Form.js`), fora de escopo
                            deste dialog. */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px',
                            mt: 2,
                            p: '12px 14px',
                            borderRadius: '12px',
                            backgroundColor: 'background.subtle',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}>
                            <Info size={16} strokeWidth={2} color={theme.palette.text.disabled} style={{ marginTop: 1, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.5 }}>
                                <Box component="span" sx={{ display: 'block', fontWeight: 700, color: 'text.primary', fontSize: '12.5px', mb: '2px' }}>
                                    Editor de grupos de inspeção
                                </Box>
                                A montagem e a reordenação dos grupos e itens de inspeção deste template é feita em uma tela dedicada de arrastar-e-soltar, que ainda não foi implementada — item separado do redesign.
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>

            <Box sx={{
                p: '16px 22px 20px',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 1.25,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
            }}>
                <Button
                    variant="outlined"
                    startIcon={<ListChecks size={15} strokeWidth={2} />}
                    onClick={() => onStubGroupsEditor?.()}
                    sx={{ mr: 'auto' }}
                >
                    Abrir editor de grupos
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<Maximize2 size={14} strokeWidth={2} />}
                    onClick={() => {
                        handleClose();
                        navigate(`/checklist/${checklistId}/view`);
                    }}
                    disabled={!checklistId}
                >
                    Ver detalhes completos
                </Button>
                <Button variant="outlined" onClick={handleClose}>Fechar</Button>
                {canEdit && (
                    <Button
                        variant="contained"
                        onClick={() => {
                            const chk = checklist;
                            handleClose();
                            if (onRequestEdit) onRequestEdit(chk);
                        }}
                    >
                        Editar
                    </Button>
                )}
            </Box>
        </Dialog>
    );
}
